import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import FullScreenTripTimeline from "@/components/trip-card/FullScreenTripTimeline";
import { useEffect, useState } from "react";
import { Trip, TripElement } from "@/types/trip";
import { getFlightStatus } from "@/utils/flightStatus";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import DesktopLayout from "@/components/layout/DesktopLayout";
import { useIsMobile } from "@/hooks/use-mobile";
import { motion } from "framer-motion";
import { useBasicTripCardData } from "@/hooks/trip-card/useBasicTripCardData";
import { useWeatherData } from "@/hooks/trip-card/useWeatherData";
import { useActivitiesData } from "@/hooks/trip-card/useActivitiesData";
import { useFoodData } from "@/hooks/trip-card/useFoodData";
import { WeatherDialog } from "@/components/trip-card/dialogs/WeatherDialog";
import { ActivitiesDialog } from "@/components/trip-card/dialogs/ActivitiesDialog";
import { FoodDialog } from "@/components/trip-card/dialogs/FoodDialog";
import DeleteTrip from "@/components/trip-card/DeleteTrip";
import { FlutterUtils } from "@/hooks/useFlutterBridge";

const TripTimelinePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [checkingFlightStatus, setCheckingFlightStatus] = useState(false);
  
  // Get trip from location state
  const trip: Trip = location.state?.trip;

  // We need to recreate the getAirportInfo function since we couldn't pass it through router state
  const { 
    getAirportInfo, 
    getDestinationName,
    getDestinationImage,
    isLoading,
    mainDestinationCode
  } = trip ? useBasicTripCardData(trip) : { 
    getAirportInfo: () => ({}), 
    getDestinationName: () => "",
    getDestinationImage: () => "",
    isLoading: false,
    mainDestinationCode: ""
  };

  let isFlutterWebapp = FlutterUtils.isFlutterWebapp()
  
  // Update the trip's identifiedDestination with the value from getDestinationName()
  useEffect(() => {
    if (trip && !isLoading) {
      // Always call getDestinationName() directly for consistent behavior
      const destinationName = getDestinationName();
      
      console.debug("🔄 TripTimelinePage.tsx useEffect - updating identifiedDestination:", {
        tripId: trip.id,
        oldIdentifiedDestination: trip.identifiedDestination,
        newDestinationName: destinationName,
        isMobile,
        computationLocation: "TripTimelinePage.tsx useEffect"
      });
      
      // Only update if there's a change to avoid infinite loops
      if (trip.identifiedDestination !== destinationName) {
        trip.identifiedDestination = destinationName;
        trip.destinationImage = getDestinationImage();
        
        console.debug("🏙️ TripTimelinePage setting identifiedDestination:", {
          tripId: trip.id,
          isMobile,
          destinationName,
          imageUpdated: !!trip.destinationImage
        });
      }
    }
  }, [trip, getDestinationName, getDestinationImage, isLoading, isMobile]);
  
  // Get weather, activities, and food data hooks
  // Use optional chaining and type guard to safely access flight details
  const getFlightArrivalCode = () => {
    if (!trip) return null;
    const flightElement = trip.elements.find(e => e.type === 'flight');
    if (!flightElement) return null;
    
    const details = flightElement.details;
    if ('arrival' in details && details.arrival?.code) {
      return details.arrival.code;
    }
    return null;
  };
  
  // ALWAYS retrieve the destination name fresh by calling the function directly
  const destinationName = trip ? getDestinationName() : "";

  console.debug("🏙️ TripTimelinePage destinationName:", {
    tripId: trip?.id,
    destinationName,
    tripIdentifiedDestination: trip?.identifiedDestination,
    getDestinationNameResult: destinationName,
    isMobile,
    computationLocation: "TripTimelinePage render phase"
  });

  const { 
    showWeather, 
    setShowWeather, 
    weatherData, 
    handleWeatherClick 
  } = trip ? useWeatherData(
    trip.id, 
    getFlightArrivalCode() || "", 
    destinationName
  ) : { showWeather: false, setShowWeather: () => {}, weatherData: null, handleWeatherClick: () => {} };

  const { 
    showActivities, 
    setShowActivities, 
    activitiesData, 
    handleActivitiesClick 
  } = trip ? useActivitiesData(
    trip.id, 
    destinationName
  ) : { showActivities: false, setShowActivities: () => {}, activitiesData: null, handleActivitiesClick: () => {} };

  const { 
    showFood, 
    setShowFood, 
    foodData, 
    handleFoodClick 
  } = trip ? useFoodData(
    trip.id, 
    destinationName
  ) : { showFood: false, setShowFood: () => {}, foodData: null, handleFoodClick: () => {} };

  useEffect(() => {
    // If no trip data is passed, redirect to home
    if (!trip) {
      navigate("/");
    }
  }, [trip, navigate]);

  const handleCheckFlightStatus = async (flightNumber: string) => {
    try {
      setCheckingFlightStatus(true);
      
      const flightIds = trip.elements
        .filter(element => element.type === 'flight')
        .map(element => (element.details as any).flightNumber)
        .filter(Boolean);
        
      if (flightIds.length === 0) {
        toast({
          variant: "destructive",
          description: t("tripCard.noFlightInfoFound", "No flight information found")
        });
        return;
      }
      
      const statusData = await getFlightStatus(flightIds);
      
      if (statusData.length === 0) {
        // toast({
        //   variant: "destructive",
        //   description: t("tripCard.noFlightStatusAvailable", "No flight status information available")
        // });
        return;
      }
      
      toast({
        title: t("tripCard.flightStatus", "Flight {{flightNumber}} Status", { flightNumber }),
        description: t("tripCard.flightInfoUpdated", "Flight information updated")
      });
    } catch (error) {
      console.error("Error checking flight status:", error);
      toast({
        variant: "destructive",
        description: t("tripCard.failedFlightStatus", "Failed to check flight status. Please try again.")
      });
    } finally {
      setCheckingFlightStatus(false);
    }
  };

  // Enhanced debug log to verify destination name and device mode
  console.debug("🏙️ TripTimelinePage DEBUG:", {
    tripId: trip?.id,
    isMobile,
    mainDestinationCode,
    raw_destinationName: destinationName,
    tripIdentifiedDestination: trip?.identifiedDestination,
    getDestinationNameResult: destinationName,
    finalDisplayedDestination: destinationName || t("tripCard.tripDetails", "Trip Details"),
    computationLocation: "TripTimelinePage final debug"
  });

  // For mobile, we shouldn't normally reach this page, but handle gracefully
  if (isMobile) {
    return (
      <div className="p-4">
        <button 
          onClick={() => navigate("/")}
          className="mb-4 flex items-center text-travel-600"
        >
          <X className="w-5 h-5 mr-2" /> {t("common.back")}
        </button>
        {trip && (
          <FullScreenTripTimeline 
            trip={trip}
            getAirportInfo={getAirportInfo}
            handleCheckFlightStatus={handleCheckFlightStatus}
            onWeatherClick={handleWeatherClick}
            onActivitiesClick={handleActivitiesClick}
            onFoodClick={handleFoodClick}
          />
        )}
      </div>
    );
  }

  // Desktop view with animation
  return (
    <DesktopLayout>
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ 
          type: "spring", 
          stiffness: 300, 
          damping: 30,
          duration: 0.5 
        }}
        className="bg-white rounded-lg shadow-sm"
      >
        <div className="sticky top-0 z-10 flex justify-between items-center p-4 bg-white/80 backdrop-blur-md border-b">
          <h2 className="text-xl font-bold text-travel-700">
            {destinationName || t("tripCard.tripDetails", "Trip Details")}
          </h2>
          <motion.button 
            onClick={() => navigate("/")}
            className="rounded-full p-2 hover:bg-gray-100 text-travel-500 active:bg-travel-50 transition-colors duration-200"
            aria-label="Close"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <X className="w-5 h-5" />
          </motion.button>
        </div>
        
        {trip && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="h-[calc(100vh-180px)] overflow-y-auto"
          >
            <FullScreenTripTimeline 
              trip={trip}
              getAirportInfo={getAirportInfo}
              handleCheckFlightStatus={handleCheckFlightStatus}
              onWeatherClick={handleWeatherClick}
              onActivitiesClick={handleActivitiesClick}
              onFoodClick={handleFoodClick}
            />
          </motion.div>
        )}
      </motion.div>

      {/* Dialogs */}
      {trip && (
        <>
          <WeatherDialog 
            showWeather={showWeather} 
            setShowWeather={setShowWeather} 
            weatherData={weatherData} 
            destinationName={destinationName}
          />
          
          <ActivitiesDialog 
            showActivities={showActivities} 
            setShowActivities={setShowActivities} 
            activitiesData={activitiesData} 
            destinationName={destinationName}
            tripId={trip.id}
          />
          
          <FoodDialog 
            showFood={showFood} 
            setShowFood={setShowFood} 
            foodData={foodData} 
            destinationName={destinationName}
            tripId={trip.id}
          />
        </>
      )}
    </DesktopLayout>
  );
};

export default TripTimelinePage;
